import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FeedAds } from "components/ads/FeedAds";
import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";

import { IGif, INicheMap } from "services/types";
import { useForYouFeedQuery } from "queries";
import { PreviewFeed } from "components/feed/PreviewFeed";
import { SuggestedNiches } from "components/widgets/SuggestedNiches";
import { SuggestedCreators } from "components/widgets/SuggestedCreators";

import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { IUserList } from "components/feed/GifList";
import { filterUsersList } from "utils/filterUsersList";
import { ViewRegion } from "utils/metrics";
import { mixWithBoosted } from "utils/boost";
import { debugLoadingGifs } from "utils/bugSnagHelper";
import { AxiosError } from "axios";
import { useNewBoostedGifs } from "hooks/useNewBoostedGifs";
import InjectionFeed from "components/feed/InjectionFeed";

const NICHES_POSITION = 9;
const NICHES_2_POSITION = 1;
const CREATORS_POSITION = 4;

export const PersonalFeed = () => {
  const [page, setPage] = useState<number>(1);
  const prevGifs = useRef<IGif[]>([]);
  const prevPages = useRef<number>(1);
  const prevUsers = useRef<IUserList>({});
  const prevNiches = useRef<INicheMap>({});

  const { data, isSuccess, isError, isLoading, error } = useForYouFeedQuery({
    page,
    count: 50,
  });

  const { boostedGifs, boostedUsers } = useNewBoostedGifs();

  const gifs = useMemo(() => {
    if (isSuccess && data) {
      const filteredGifs = filterGifsList(prevGifs.current, data.gifs);
      prevGifs.current = mixWithBoosted({
        gifs: filteredGifs,
        boostedPostData: boostedGifs,
        tiles: false,
      });
    }

    return prevGifs.current;
  }, [isSuccess, data, boostedGifs]);

  const pages = useMemo(() => {
    if (isSuccess && data) {
      prevPages.current = data.pages;
    }

    return prevPages.current;
  }, [isSuccess, data]);

  const users = useMemo(() => {
    if (isSuccess && data) {
      prevUsers.current = filterUsersList(prevUsers.current, [...data.users, ...boostedUsers]);
    }

    return prevUsers.current;
  }, [isSuccess, data, boostedUsers]);

  const niches = useMemo(() => {
    if (isSuccess && data && data.niches) {
      prevNiches.current = filterNichesList(prevNiches.current, data.niches);
    }

    return prevNiches.current;
  }, [isSuccess, data]);

  // TODO: debug 'error loading gif'
  useEffect(() => {
    if (isError) {
      debugLoadingGifs(error as AxiosError);
    }
  }, [isError, error]);

  const hasMore = useMemo(() => page < pages, [page, pages]);

  const fetchMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const injector = useCallback((pos: number): JSX.Element => {
    const children: ReactNode[] = [];

    if (pos === NICHES_2_POSITION) {
      children.push(<SuggestedNiches key="SuggestedNichesKey1" page={1}/>);
    }

    if (pos === NICHES_POSITION) {
      children.push(<SuggestedNiches key="SuggestedNichesKey2" page={2}/>);
    }

    if (pos === CREATORS_POSITION) {
      children.push(<SuggestedCreators key="SuggestedCreators" />);
    }

    children.push(
      <FeedAds
        key={`feedAds-${pos}`}
        position={pos}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, []);

  return (
    <Fragment>
      <PreviewFeed>
        <InjectionFeed
          gifs={gifs}
          users={users}
          niches={niches}
          source={ViewRegion.ForYou}
          sourceId={null}
          injector={injector}
          isLoading={isLoading}
          isError={isError}
          fetchMore={fetchMore}
          hasMore={hasMore}
        />
      </PreviewFeed>
    </Fragment>
  );
};
